import { BreakpointObserver } from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuctionService } from '@core/services/auction.service';
import { AuthService } from '@core/services/auth.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { BidElementComponent } from '@shared/components/bid-element/bid-element.component';
import { BidShowcaseComponent } from '@shared/components/bid-showcase/bid-showcase.component';
import { SlideshowComponent } from '@shared/components/slideshow/slideshow.component';
import { LanguageService } from '@shared/services/language.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { SSR_IS_MOBILE } from '@shared/tokens/server.token';
import { distinctUntilChanged, map } from 'rxjs';
import {
  AuctionPrice,
  AuctionView,
  HomePageSectionCardDto,
  HomePageSectionFrontendDto,
  HomePageSectionResourceCardDto,
  HomePageSectionResourceDto,
} from '../articles-manager/interfaces/auction.interface';
import { HomeService } from './services/home.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    imports: [
        BidShowcaseComponent,
        SlideshowComponent,
        BidElementComponent,
        RouterModule,
        TranslatePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@UntilDestroy()
export class HomeComponent implements OnInit, OnDestroy {
  auctionList = signal<Array<AuctionView>>([]);
  idAuctionList = signal<Array<number>>([]);
  sections = signal<Array<HomePageSectionFrontendDto>>([]);
  readonly activeSections = computed(() =>
    this.sections().filter((section) => section.isActive)
  );

  private readonly languageCode = computed(
    () =>
      this.languageServices.selectedLanguageSignal()?.code?.toLowerCase?.() ??
      'en'
  );
  private readonly languageId = computed(() =>
    this.languageCode() === 'it' ? 1 : 2
  );
  private readonly isItalian = computed(() => this.languageCode() === 'it');

  private readonly sectionCarouselItemsMap = computed(() => {
    const isItalian = this.isItalian();

    const map = new Map<
      number,
      Array<NonNullable<HomePageSectionFrontendDto['carouselItems']>[number]>
    >();

    for (const section of this.sections()) {
      map.set(
        section.id,
        (section.carouselItems ?? [])
          .filter(
            (item) =>
              item.isActive !== false &&
              this.isCarouselItemInActiveDateRange(item)
          )
          .map((item) => {
            const itemData = item as Record<string, string | null | undefined>;
            const mobileImageUrlEn =
              itemData['mobileImageUrl'] ?? itemData['imageUrlMobile'] ?? null;
            const mobileImageUrlIt =
              itemData['mobileImageUrlIt'] ??
              itemData['imageUrlMobileIt'] ??
              null;

            return {
              ...item,
              imageUrl: this.resolveLocalizedValue(
                item.imageUrl,
                itemData['imageUrlIt'],
                isItalian
              ),
              mobileImageUrl: this.resolveLocalizedValue(
                mobileImageUrlEn,
                mobileImageUrlIt,
                isItalian
              ),
              youtubeUrl: this.resolveLocalizedValue(
                item.youtubeUrl,
                itemData['youtubeUrlIt'],
                isItalian
              ),
              youtubeUrlMobile: this.resolveLocalizedValue(
                item.youtubeUrlMobile,
                itemData['youtubeUrlMobileIt'],
                isItalian
              ),
            };
          })
      );
    }

    return map;
  });

  private readonly sectionResourceCardsMap = computed(() => {
    const map = new Map<number, HomePageSectionResourceCardDto[]>();

    for (const section of this.sections()) {
      map.set(
        section.id,
        (section.resourceCards ?? [])
          .filter((card) => card.isActive !== false)
          .sort(
            (left, right) => (left.orderIndex ?? 0) - (right.orderIndex ?? 0)
          )
      );
    }

    return map;
  });

  private readonly sectionCardsMap = computed(() => {
    const map = new Map<number, HomePageSectionCardDto[]>();

    for (const section of this.sections()) {
      map.set(
        section.id,
        (section.sectionCards ?? [])
          .filter((card) => card.isActive !== false)
          .sort(
            (left, right) => (left.orderIndex ?? 0) - (right.orderIndex ?? 0)
          )
      );
    }

    return map;
  });

  private readonly resourceCardImageUrlMap = computed(() => {
    const isItalian = this.isItalian();
    const isMobile = this.isMobileView();
    const map = new Map<HomePageSectionResourceCardDto, string>();
    for (const cards of this.sectionResourceCardsMap().values()) {
      for (const card of cards) {
        map.set(card, this.computeCardImageUrl(card, isItalian, isMobile));
      }
    }
    return map;
  });

  private readonly sectionCardImageUrlMap = computed(() => {
    const isItalian = this.isItalian();
    const isMobile = this.isMobileView();
    const map = new Map<HomePageSectionCardDto, string>();
    for (const cards of this.sectionCardsMap().values()) {
      for (const card of cards) {
        map.set(card, this.computeCardImageUrl(card, isItalian, isMobile));
      }
    }
    return map;
  });

  private readonly resourceCardRouteMap = computed(() => {
    const languageCode = this.languageCode();
    const map = new Map<
      HomePageSectionResourceCardDto,
      Array<string | number> | null
    >();
    for (const cards of this.sectionResourceCardsMap().values()) {
      for (const card of cards) {
        if (!card.resourceType || !card.resourceId) {
          map.set(card, null);
          continue;
        }
        map.set(
          card,
          this.buildResourceRouteFor(card.resourceType, card, languageCode)
        );
      }
    }
    return map;
  });

  private _refreshTimer: ReturnType<typeof setInterval> | null = null;
  private readonly _refreshTimerIntervalLogged = 2000;
  private readonly _refreshTimerIntervalNotLogged = 30_000;

  private readonly visibilityChangeListener = () => {
    // No point polling prices into a tab nobody is looking at, and the
    // authenticated cadence is 2s. Re-arming on `visible` also picks up any
    // auth change that happened while we were hidden.
    if (document.visibilityState === 'visible') {
      this.restartRefreshTimer();
    } else {
      this.clearRefreshTimer();
    }
  };

  private readonly homeService = inject(HomeService);
  private readonly languageServices = inject(LanguageService);
  private readonly auctionService = inject(AuctionService);
  private readonly datePipe = inject(DatePipe);
  private readonly authService = inject(AuthService);
  private readonly platformService = inject(PlatformService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly ngZone = inject(NgZone);
  private readonly ssrIsMobile = inject(SSR_IS_MOBILE, { optional: true });

  // Client-side re-bootstrap has no access to SSR_IS_MOBILE (only provided in
  // server.ts's per-request providers), so without this it always restarts at
  // `false` and only self-corrects once ngOnInit's matchMedia subscription
  // fires a tick later — long enough to flash the desktop video src before
  // swapping to the mobile one. Reading matchMedia synchronously here removes
  // that gap entirely.
  isMobileView = signal(
    this.platformService.isBrowser()
      ? window.matchMedia('(max-width: 768px)').matches
      : this.ssrIsMobile ?? false
  );

  private lastLoadedLanguageId?: number;

  constructor() {
    // `effect()` always defers its first run to a microtask after the
    // component's first render, even though getHomeData() resolves
    // synchronously here (TransferState cache hit via `of(...)`). Waiting for
    // the effect meant the first client render used the empty auctionList/
    // sections defaults — wiping the SSR-populated cards — before the effect
    // repopulated them a tick later, reloading every card image. Loading
    // synchronously in the constructor keeps the client's first render
    // already populated, matching what hydration reconciles against.
    this.lastLoadedLanguageId = this.languageId();
    this.loadHomeData(this.lastLoadedLanguageId);

    effect(() => {
      const language = this.languageId();

      if (language === this.lastLoadedLanguageId) {
        return;
      }

      this.lastLoadedLanguageId = language;
      this.loadHomeData(language);
    });

    // The price-refresh cadence is baked into `setInterval` at creation time,
    // so a timer armed while anonymous keeps the 30s cadence forever — a user
    // who logs in after load never gets the 2s one (and vice versa). Re-arm
    // whenever the auth state flips. This is the timer's only driver; it used
    // to be started from the data-load subscription above, which meant the
    // cadence was fixed by whatever the auth state happened to be back then.
    // Arming before auctions load is harmless: `updateAuctionPrice()` returns
    // early while `idAuctionList()` is empty.
    effect(() => {
      this.authService.isAuthenticated();
      this.restartRefreshTimer();
    });
  }

  private loadHomeData(language: number): void {
    if (!Number.isFinite(language) || language <= 0) {
      return;
    }

    this.homeService
      .getHomeData(language)
      .pipe(untilDestroyed(this))
      .subscribe(({ auctionList, sections }) => {
        const auctionIds = auctionList
          .filter((auction) => !auction.isFinished)
          .map((auction) => auction.idAuction);

        this.idAuctionList.set([...new Set(auctionIds)]);
        this.auctionList.set(auctionList);

        this.sections.set(sections);
      });
  }

  ngOnInit(): void {
    this.clearRefreshTimer();

    this.platformService.runOnBrowser(() => {
      document.addEventListener(
        'visibilitychange',
        this.visibilityChangeListener
      );

      // CDK's matchMedia is a no-op on the server (always reports no match),
      // so subscribing during SSR would overwrite the UA-seeded isMobileView
      // with a false-negative before the response is even sent.
      this.breakpointObserver
        .observe(['(max-width: 768px)'])
        .pipe(
          map((result) => result.matches),
          distinctUntilChanged(),
          untilDestroyed(this)
        )
        .subscribe((isMobile) => {
          this.isMobileView.set(isMobile);
        });
    });
  }

  startTimer() {
    this.platformService.runOnBrowser(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (!this._refreshTimer) {
        // Runs for as long as the homepage is open, so it must stay outside
        // Angular's zone — otherwise NgZone.hasPendingMacrotasks never
        // returns to false and ApplicationRef.whenStable() (which hydration
        // waits on) never resolves, tripping NG0506 on every load.
        this.ngZone.runOutsideAngular(() => {
          this._refreshTimer = setInterval(
            () => {
              this.ngZone.run(() => this.updateAuctionPrice());
            },
            this.authService.isAuthenticated()
              ? this._refreshTimerIntervalLogged
              : this._refreshTimerIntervalNotLogged
          );
        });
      }
    });
  }

  /** Tears the interval down first, so a changed cadence actually takes effect. */
  private restartRefreshTimer(): void {
    this.platformService.runOnBrowser(() => {
      this.clearRefreshTimer();
      this.startTimer();
    });
  }

  updateAuctionPrice() {
    const ids = this.idAuctionList();
    if (!ids.length) {
      return;
    }

    this.auctionService
      .getCurrentPrices(ids)
      .pipe(untilDestroyed(this))
      .subscribe((dataR) => {
        if (!dataR?.length) {
          return;
        }

        const dataMap = dataR.reduce(
          (
            acc: { [key: number]: AuctionPrice },
            item: AuctionPrice
          ): { [key: number]: AuctionPrice } => {
            acc[item.idAuction] = item;
            return acc;
          },
          {} as { [key: number]: AuctionPrice }
        );

        const current = this.auctionList();
        let auctionChanged = false;
        const auc = current.map((auction) => {
          const next = this.applyPriceUpdate(auction, dataMap);
          if (next !== auction) {
            auctionChanged = true;
          }
          return next;
        });

        if (auctionChanged) {
          this.auctionList.set(auc);
        }

        this.updateSectionAuctionPrices(dataMap);
      });
  }

  private updateSectionAuctionPrices(dataMap: { [key: number]: AuctionPrice }) {
    let anyChanged = false;
    const next = this.sections().map((section) => {
      const updated = this.updateSectionPrices(section, dataMap);
      if (updated !== section) {
        anyChanged = true;
      }
      return updated;
    });

    if (anyChanged) {
      this.sections.set(next);
    }
  }

  private updateSectionPrices(
    section: HomePageSectionFrontendDto,
    dataMap: { [key: number]: AuctionPrice }
  ): HomePageSectionFrontendDto {
    if (!section.auctions?.length) {
      return section;
    }

    let changed = false;
    const auctions = section.auctions.map((auction) => {
      const next = this.applyPriceUpdate(auction, dataMap);
      if (next !== auction) {
        changed = true;
      }
      return next;
    });

    if (!changed) {
      return section;
    }

    return {
      ...section,
      auctions,
    };
  }

  private applyPriceUpdate(
    auction: AuctionView,
    dataMap: { [key: number]: AuctionPrice }
  ): AuctionView {
    const price = dataMap[auction.idAuction];
    if (!price || auction.basePrice === price.currentMaxOfferedPrice) {
      return auction;
    }

    return {
      ...auction,
      endDateView:
        this.datePipe.transform(price.endDateView, 'yyyy-MM-ddTHH:mm', 'UTC') ??
        auction.endDateView,
      currentMaxOfferedPrice: price.currentMaxOfferedPrice,
      hasUserBided: price?.hasUserBided ?? auction.hasUserBided,
      isUserBestBider: price?.isUserBestBider ?? auction.isUserBestBider,
      reservePriceReached:
        price?.reservePriceReached ?? auction.reservePriceReached,
      userMaxOfferedPrice:
        price?.userMaxOfferedPrice ?? auction.userMaxOfferedPrice,
    };
  }

  ngOnDestroy() {
    this.clearRefreshTimer();

    this.platformService.runOnBrowser(() => {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeListener
      );
    });
  }

  private clearRefreshTimer(): void {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  getSectionAuctions(section: HomePageSectionFrontendDto): AuctionView[] {
    return this.homeService.getSectionAuctions(section);
  }

  getSectionCarouselItems(section: HomePageSectionFrontendDto) {
    return this.sectionCarouselItemsMap().get(section.id) ?? [];
  }

  shouldDeferCarouselSection(
    section: HomePageSectionFrontendDto,
    sectionIndex: number
  ): boolean {
    if (section.sectionType !== 'carousel') {
      return sectionIndex >= 3;
    }

    if (sectionIndex < 3) {
      return false;
    }

    return true;
  }

  shouldRenderCarouselYoutubeImmediately(
    section: HomePageSectionFrontendDto
  ): boolean {
    if (section.sectionType !== 'carousel') {
      return false;
    }

    return this.carouselSectionHasYoutube(section);
  }

  private carouselSectionHasYoutube(
    section: HomePageSectionFrontendDto
  ): boolean {
    const carouselItems = this.getSectionCarouselItems(section);

    return carouselItems.some((item) => {
      const youtubeUrl = item.youtubeUrl?.trim();
      const youtubeUrlMobile = item.youtubeUrlMobile?.trim();
      return !!youtubeUrl || !!youtubeUrlMobile;
    });
  }

  getSectionResourceCards(
    section: HomePageSectionFrontendDto
  ): HomePageSectionResourceCardDto[] {
    return this.sectionResourceCardsMap().get(section.id) ?? [];
  }

  getSectionCards(
    section: HomePageSectionFrontendDto
  ): HomePageSectionCardDto[] {
    return this.sectionCardsMap().get(section.id) ?? [];
  }

  getResourceCardImageUrl(card: HomePageSectionResourceCardDto): string {
    return this.resourceCardImageUrlMap().get(card) ?? '';
  }

  getSectionCardImageUrl(card: HomePageSectionCardDto): string {
    return this.sectionCardImageUrlMap().get(card) ?? '';
  }

  private computeCardImageUrl(
    card: HomePageSectionResourceCardDto | HomePageSectionCardDto,
    isItalian: boolean,
    isMobile: boolean
  ): string {
    const cardData = card as Record<string, string | null | undefined>;

    const mobileImageEn =
      cardData['mobileImageUrl'] ?? cardData['imageUrlMobile'] ?? null;
    const mobileImageIt =
      cardData['mobileImageUrlIt'] ?? cardData['imageUrlMobileIt'] ?? null;

    const desktopLocalized = this.resolveLocalizedValue(
      card.imageUrl,
      cardData['imageUrlIt'],
      isItalian
    );
    const mobileLocalized = this.resolveLocalizedValue(
      mobileImageEn,
      mobileImageIt,
      isItalian
    );

    if (isMobile) {
      return mobileLocalized ?? desktopLocalized ?? '';
    }

    return desktopLocalized ?? mobileLocalized ?? '';
  }

  getAuctionDisplayList(section: HomePageSectionFrontendDto): AuctionView[] {
    const auctionRenderLimit = this.getAuctionRenderLimit(section);
    const sectionAuctions = this.getSectionAuctions(section);

    if (sectionAuctions.length > 0) {
      return sectionAuctions.slice(0, auctionRenderLimit);
    }

    if (section.sectionType === 'bid_showcase') {
      return this.auctionList().slice(0, auctionRenderLimit);
    }

    return [];
  }

  private getAuctionRenderLimit(section: HomePageSectionFrontendDto): number {
    const configuredLimit = Number(section.auctionRenderLimit ?? 42);

    if (!Number.isFinite(configuredLimit) || configuredLimit <= 0) {
      return 42;
    }

    return Math.floor(configuredLimit);
  }

  getResourceRoute(
    section: HomePageSectionFrontendDto
  ): Array<string | number> | null | string {
    const type = section.sectionType;
    const languageCode = this.languageCode();

    const resource = section.resource;

    if (type === 'resource' && resource?.resourceType && resource?.resourceId) {
      return this.buildResourceRouteFor(
        resource.resourceType,
        resource,
        languageCode
      );
    }

    if (type === 'resource_cards') {
      return ['/', languageCode, 'resources', section.id];
    }

    return null;
  }

  getResourceCardRoute(
    card: HomePageSectionResourceCardDto
  ): Array<string | number> | null {
    return this.resourceCardRouteMap().get(card) ?? null;
  }

  getResourceCardTitle(card: HomePageSectionResourceCardDto): string {
    const languageCode = this.languageCode();

    if (languageCode === 'it' && card.title?.it) {
      return card.title.it;
    }

    if (languageCode === 'en' && card.title?.en) {
      return card.title.en;
    }

    return (
      card.title?.base ??
      card.title?.it ??
      card.title?.en ??
      card.resourceLabel ??
      ''
    );
  }

  private buildResourceRouteFor(
    resourceType: string,
    resource: Pick<HomePageSectionResourceDto, 'trackingSlug' | 'resourceId'>,
    languageCode: string
  ): Array<string | number> | null {
    const tracking = resource.trackingSlug?.trim();
    const fallbackId = resource.resourceId;

    if (resourceType === 'tag') {
      const slug = tracking || String(fallbackId);
      return ['/', languageCode, 'tags', slug];
    }

    if (resourceType === 'promoter') {
      return ['/', languageCode, 'promoter', Number(tracking || fallbackId)];
    }

    if (resourceType === 'campaign') {
      return ['/', languageCode, 'campaign', Number(tracking || fallbackId)];
    }

    if (resourceType === 'seller') {
      return ['/', languageCode, 'seller', Number(tracking || fallbackId)];
    }

    return null;
  }

  getSectionTitle(section: HomePageSectionFrontendDto): string {
    const languageCode = this.languageCode();
    const title = this.isMobileView()
      ? section.titleMobile ?? section.title
      : section.title;

    if (languageCode === 'it' && title?.it) {
      return title.it;
    }

    if (languageCode === 'en' && title?.en) {
      return title.en;
    }

    return title?.base ?? title?.it ?? title?.en ?? '';
  }

  private resolveLocalizedValue(
    englishValue: string | null | undefined,
    italianValue: string | null | undefined,
    isItalian: boolean
  ): string | null {
    const en = englishValue?.trim() || null;
    const it = italianValue?.trim() || null;

    if (isItalian && it) {
      return it;
    }

    return en;
  }

  private isCarouselItemInActiveDateRange(
    item: NonNullable<HomePageSectionFrontendDto['carouselItems']>[number]
  ): boolean {
    const now = Date.now();
    const startDateMs = item.startDate
      ? Date.parse(item.startDate)
      : Number.NaN;
    const endDateMs = item.endDate ? Date.parse(item.endDate) : Number.NaN;

    const hasStartDate = Number.isFinite(startDateMs);
    const hasEndDate = Number.isFinite(endDateMs);

    if (hasStartDate && now < startDateMs) {
      return false;
    }

    if (hasEndDate && now > endDateMs) {
      return false;
    }

    return true;
  }
}
